/**
 * @fileoverview Shared callable API contract for `chooseTeachBackCredit`
 * (#5270), which makes one of a learner's eligible Teach it back attempts
 * the week's credit. The functions handler and the webapp client both
 * import it, so the two sides cannot drift in name, request shape, or
 * response shape.
 */
import {z} from 'zod';

import {FirestoreDocumentIdSchema} from '../firebase/types';
import {
  TeachBackAttemptIdSchema,
  TeachBackSectionWeekSchema,
} from './teachBack';

/**
 * Deployed Cloud Function name. Must match the exported function name in the
 * `sections` functions codebase.
 */
export const CHOOSE_TEACH_BACK_CREDIT_FUNCTION_NAME = 'chooseTeachBackCredit';

/** What the client sends: the attempt to submit for a week. */
export const ChooseTeachBackCreditRequestSchema = z.object({
  /** The course. */
  courseId: FirestoreDocumentIdSchema,
  /** The 1-based section week. */
  sectionWeek: TeachBackSectionWeekSchema,
  /** The learner's attempt to make the week's credit. */
  attemptId: TeachBackAttemptIdSchema,
});

/** Parsed request. */
export type ChooseTeachBackCreditRequest = z.infer<
  typeof ChooseTeachBackCreditRequestSchema
>;

/** Why the credit did not move. */
export type ChooseTeachBackCreditRefusal =
  /** The learner has no attempt with this id for the week. */
  | 'unknown-attempt'
  /**
   * The attempt can't be credit: a session that didn't pass or didn't end,
   * or a removed upload (`isTeachBackCreditEligible`).
   */
  | 'not-eligible';

/** What the callable returns. */
export type ChooseTeachBackCreditResponse =
  | {
      chosen: true;
      /** The week's credited attempt, now the one requested. */
      creditedAttemptId: string;
    }
  | {
      chosen: false;
      /** Why not. */
      reason: ChooseTeachBackCreditRefusal;
    };
